import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const { pathname } = useLocation();

  // Luk menuen ved sideskift
  useEffect(() => {
    setIsOpen(false);
    setMobileDropdownOpen(false);
  }, [pathname]);

  const menuItems = [
    { label: 'Forside', href: '/' },
    { label: 'Vi tilbyder', href: '/vi-tilbyder' },
    { label: 'Anmeldelser', href: '/anmeldelser' },
    { label: 'Galleri', href: '/referencegalleri' },
    { label: 'Nyheder', href: '/nyheder' },
    { label: 'Profil', href: '/profil' },
    { label: 'Kontakt', href: '/kontakt' }
  ];

  const subServices = [
    { label: 'Nybyggeri', href: '/vi-tilbyder/nybyggeri' },
    { label: 'Om- og tilbygning', href: '/vi-tilbyder/om-tilbygning' },
    { label: 'Murerafdeling', href: '/vi-tilbyder/murerafdeling' },
    { label: 'Vinduer og døre', href: '/vi-tilbyder/vinduer-doere' },
    { label: 'Glarmester', href: '/vi-tilbyder/glarmester' },
    { label: 'Forsikringsskader', href: '/vi-tilbyder/forsikringsskader' },
    { label: 'Terrasser og altaner', href: '/vi-tilbyder/terrasser-altaner' },
    { label: 'Erhverv', href: '/vi-tilbyder/erhverv' },
    { label: 'Kirker', href: '/vi-tilbyder/kirker' },
    { label: 'Mindre opgaver', href: '/vi-tilbyder/mindre-opgaver' },
    { label: 'Diverse', href: '/vi-tilbyder/diverse' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-black text-white shadow-lg border-b border-white/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-center justify-between min-h-[96px] py-2">
          {/* Logo Badge (Venstre side som på skt-byg.dk) - gjort større og rækker ned i hero */}
          <Link
            to="/"
            className="relative z-50 flex flex-col items-center select-none shrink-0 translate-y-3 sm:translate-y-4 md:translate-y-5"
            aria-label="SKT-byg Forside"
          >
            <div className="bg-black/95 px-3.5 sm:px-4 pt-2.5 pb-1.5 shadow-2xl">
              <img
                src="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/logo-hvid.png"
                alt="SKT Byg - 20 91 00 19"
                className="h-14 sm:h-16 md:h-20 w-auto object-contain drop-shadow"
              />
            </div>
            <div className="w-[105%] bg-brand-accent text-white text-center py-1.5 px-3.5 shadow-xl -rotate-[1.5deg] transform origin-center">
              <span className="text-[11px] sm:text-xs md:text-[13px] font-semibold tracking-wider uppercase whitespace-nowrap block">
                Din lokale tømrer
              </span>
            </div>
          </Link>

          {/* Højre side (Desktop): Navigation og CTA placeret i bunden af headeren */}
          <div className="hidden min-[1150px]:flex items-center gap-1 self-end pb-2">
            <nav className="flex items-center gap-1">
              {menuItems.map((item) => {
                if (item.href === '/vi-tilbyder') {
                  return (
                    <div key={item.href} className="relative group">
                      <NavLink
                        to={item.href}
                        className={({ isActive }) => `
                          px-3.5 py-2 text-xs uppercase tracking-wider font-bold transition-colors duration-200 flex items-center gap-1.5
                          ${isActive ? 'text-brand-accent' : 'text-zinc-200 hover:text-white'}
                        `}
                      >
                        <span>{item.label}</span>
                        <i className="fa-solid fa-chevron-down text-[10px] opacity-70 group-hover:rotate-180 transition-transform duration-200"></i>
                      </NavLink>
                      {/* Dropdown Menu */}
                      <div className="absolute left-0 top-full hidden group-hover:block bg-brand-dark/95 backdrop-blur-md text-white border border-white/10 shadow-2xl min-w-[220px] z-50 py-1">
                        {subServices.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            className="block px-4 py-2.5 text-xs font-semibold text-zinc-300 hover:text-white hover:bg-brand-accent transition-colors duration-150"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={({ isActive }) => `
                      px-3.5 py-2 text-xs uppercase tracking-wider font-bold transition-colors duration-200
                      ${isActive ? 'text-brand-accent' : 'text-zinc-200 hover:text-white'}
                    `}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            <a
              href="https://www.facebook.com/sktbyg/?ref=embed_page#"
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-2 text-zinc-300 hover:text-[#1877F2] transition-colors duration-200 flex items-center"
              title="Følg SKT Byg på Facebook"
              aria-label="SKT Byg på Facebook"
            >
              <i className="fa-brands fa-facebook text-lg"></i>
            </a>

            <Link
              to="/faa-et-tilbud"
              className="ml-2 px-5 py-2.5 bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-bold uppercase tracking-wider transition-all duration-200 shadow-md flex items-center gap-1.5 group/btn cursor-pointer"
            >
              <span>Få et tilbud</span>
              <span className="text-white/80 group-hover/btn:translate-x-0.5 transition-transform">»</span>
            </Link>
          </div>

          {/* Mobil knapper (Vises under 1150px) */}
          <div className="flex min-[1150px]:hidden items-center gap-2.5">
            <a
              href="tel:20910019"
              className="flex items-center gap-1.5 px-3 py-2 bg-brand-accent text-white hover:bg-brand-accent-hover text-xs font-bold transition-colors"
              aria-label="Ring til 20 91 00 19"
            >
              <i className="fa-solid fa-phone text-xs"></i>
              <span className="hidden sm:inline">20 91 00 19</span>
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-10 w-10 items-center justify-center bg-zinc-900 border border-white/10 text-white hover:text-brand-accent transition-colors cursor-pointer"
              aria-label="Åben menu"
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobil Navigation Drawer */}
      {isOpen && (
        <div className="min-[1150px]:hidden bg-zinc-950/98 border-t border-white/10 animate-fade-in shadow-2xl">
          {/* Mobil Utility Info */}
          <div className="px-6 py-3 bg-zinc-900/60 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-zinc-300">
            <a href="tel:20910019" className="flex items-center gap-2 hover:text-white">
              <i className="fa-solid fa-phone text-brand-accent"></i>
              <span>Tlf: <strong>20 91 00 19</strong></span>
            </a>
            <a href="mailto:steffen@skt-byg.dk" className="flex items-center gap-2 hover:text-white">
              <i className="fa-solid fa-envelope text-brand-accent"></i>
              <span>steffen@skt-byg.dk</span>
            </a>
            <a
              href="https://www.facebook.com/sktbyg/?ref=embed_page#"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-zinc-300 hover:text-[#1877F2]"
            >
              <i className="fa-brands fa-facebook text-sm text-[#1877F2]"></i>
              <span>Facebook</span>
            </a>
          </div>

          <nav className="flex flex-col px-6 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
            {menuItems.map((item) => {
              if (item.href === '/vi-tilbyder') {
                return (
                  <div key={item.href} className="flex flex-col border-b border-white/5 pb-1">
                    <div className="flex items-center justify-between py-2 text-sm font-semibold tracking-wide text-zinc-200">
                      <NavLink
                        to={item.href}
                        className={({ isActive }) => isActive ? 'text-white font-bold' : ''}
                      >
                        {item.label}
                      </NavLink>
                      <button
                        onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                        className="p-2 text-white/60 hover:text-white cursor-pointer"
                        aria-label="Fold ud"
                      >
                        <i className={`fa-solid ${mobileDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`}></i>
                      </button>
                    </div>
                    {mobileDropdownOpen && (
                      <div className="pl-4 my-1 flex flex-col border-l-2 border-brand-accent space-y-1">
                        {subServices.map((sub) => (
                          <NavLink
                            key={sub.href}
                            to={sub.href}
                            className={({ isActive }) => `
                              py-1.5 text-xs font-medium transition-colors
                              ${isActive ? 'text-white font-bold' : 'text-zinc-400 hover:text-brand-accent'}
                            `}
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) => `
                    py-2.5 text-sm font-semibold tracking-wide transition-colors border-b border-white/5
                    ${isActive ? 'text-white font-bold' : 'text-zinc-300 hover:text-brand-accent'}
                  `}
                >
                  {item.label}
                </NavLink>
              );
            })}
            <div className="pt-4">
              <Link
                to="/faa-et-tilbud"
                className="w-full py-3 bg-brand-accent hover:bg-brand-accent-hover text-white text-center font-bold uppercase tracking-wider text-xs shadow-md block transition-colors cursor-pointer"
              >
                Få et tilbud »
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
